import { ObjectId } from "@novorender/api";
import { ObjectData } from "@novorender/data-js-api";
import { Bookmark } from "@novorender/data-js-api";
import pMap from "p-map";

import { highlightCollectionKeys } from "contexts/highlightCollections";
import { ExtendedMeasureEntity, PublicIdMap, ResolvedIdMap, ResolvedPublicIds } from "types/misc";
import { AppObjectDB } from "utils/appObjectDb";
import { uniqueArray } from "utils/misc";
import { searchByPatterns } from "utils/search";

export async function collectPublicIds(
    bm: Pick<Bookmark, "explorerState">,
    db: AppObjectDB,
    abortSignal: AbortSignal,
): Promise<PublicIdMap> {
    const allIds = collectAllObjectIds([bm]);
    return buildPublicIdMap({ objectIds: allIds, db, abortSignal });
}

/**
 * Collects all internal ObjectIds referenced across all bookmarks.
 */
export function collectAllObjectIds(bookmarks: Pick<Bookmark, "explorerState">[]): number[] {
    const ids: ObjectId[] = [];

    for (const bm of bookmarks) {
        if (!bm.explorerState) {
            continue;
        }

        const { objects, measurements, alignment, propertyTree, forms } = bm.explorerState;

        for (const collection of [objects.hidden.ids, objects.highlighted.ids, objects.selectionBasket.ids]) {
            collection.forEach((id) => ids.push(id));
        }

        for (const key of highlightCollectionKeys) {
            objects.highlightCollections[key]?.ids.forEach((id) => ids.push(id));
        }

        if (objects.mainObject.id !== undefined) {
            ids.push(objects.mainObject.id);
        }

        if (propertyTree) {
            propertyTree.groups.forEach((g) => g.ids.forEach((id) => ids.push(id)));
        }

        if (measurements.manhole.id !== undefined) {
            ids.push(measurements.manhole.id);
        }

        if (measurements.manhole.collisionTarget?.selected.id !== undefined) {
            ids.push(measurements.manhole.collisionTarget.selected.id);
        }

        if (forms?.selectedFormObjectId !== undefined) {
            ids.push(forms.selectedFormObjectId);
        }

        if (alignment?.followObject) {
            alignment.followObject.ids.forEach((id) => ids.push(id));

            if (
                alignment.followObject.selectedEntity &&
                "ObjectId" in alignment.followObject.selectedEntity &&
                alignment.followObject.selectedEntity.ObjectId >= 0
            ) {
                ids.push(alignment.followObject.selectedEntity.ObjectId);
            }
        }

        if (Array.isArray(measurements.measure.entities[0])) {
            ids.push(
                ...(measurements.measure.entities as ExtendedMeasureEntity[][])
                    .flat()
                    .flatMap((e) => ("ObjectId" in e && e.ObjectId >= 0 ? [e.ObjectId] : [])),
            );
        }

        ids.push(
            ...("pointLines" in measurements.pointLine
                ? (measurements.pointLine.pointLines
                      ?.flat()
                      .flatMap((p) =>
                          typeof p === "object" && !Array.isArray(p) && p.objectId !== undefined ? [p.objectId] : [],
                      ) ?? [])
                : []),
        );
    }

    return uniqueArray(ids);
}

/**
 * Builds a per-bookmark ResolvedIdMap from its publicIdMap and the server-resolved resolvedPublicIds.
 * Used at load time (useInitBookmarks, useHandleInitialBookmark) to apply id mapping.
 *
 * publicIdMap: { internalId → publicId } stored in the v2 bookmark
 * resolvedPublicIds: { publicId → currentInternalId } from the server GET response
 */
export function buildResolvedIdMap(publicIdMap: PublicIdMap, resolvedPublicIds: ResolvedPublicIds): ResolvedIdMap {
    const result: ResolvedIdMap = {};

    for (const [oldObjectId, publicId] of Object.entries(publicIdMap)) {
        const newObjectId = resolvedPublicIds[publicId];
        if (newObjectId === undefined || newObjectId === -1) {
            continue;
        }

        result[Number(oldObjectId)] = newObjectId;
    }

    return result;
}

async function buildPublicIdMap({
    objectIds,
    db,
    abortSignal,
}: {
    objectIds: ObjectId[];
    db: AppObjectDB;
    abortSignal: AbortSignal;
}): Promise<PublicIdMap> {
    const idMap: PublicIdMap = {};

    if (objectIds.length === 0) {
        return {};
    }

    const batchSize = 500;
    const batches: string[][] = [];
    for (let i = 0; i < objectIds.length; i += batchSize) {
        batches.push(objectIds.slice(i, i + batchSize).map(String));
    }

    const concurrentRequests = 5;
    await pMap(
        batches,
        async (batch) => {
            await searchByPatterns({
                db,
                abortSignal,
                callback: (result) =>
                    result.forEach((ref) => {
                        const publicId = getPublicId(ref as ObjectData);

                        if (publicId) {
                            idMap[ref.id] = publicId;
                        }
                    }),
                full: true,
                searchPatterns: [{ property: "id", value: batch, exact: true }],
            });
        },
        { concurrency: concurrentRequests },
    );

    return idMap;
}

function getPublicId(obj: ObjectData): string | undefined {
    return obj.properties.find(([key]) => key === "Procore/InstanceId")?.[1];
}
